/**
 * Client-side PDF digital signing utilities — replicates Adobe's USB token flow.
 *
 * pdflibAddPlaceholder writes /ByteRange [0 0 0 0]. SignPdf resolves the real
 * offsets before calling BridgeSigner.sign(), so the hash computation is correct.
 */

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { pdflibAddPlaceholder } from '@signpdf/placeholder-pdf-lib';
import { SignPdf, Signer } from '@signpdf/signpdf';
import forge from 'node-forge';

const DEFAULT_SIGNATURE_LENGTH = 12000;
const BRIDGE_URL = 'http://127.0.0.1:4567';

// ── OIDs ─────────────────────────────────────────────────────────────────────
const OID_SIGNED_DATA     = '1.2.840.113549.1.7.2';
const OID_DATA            = '1.2.840.113549.1.7.1';
const OID_SHA256          = '2.16.840.1.101.3.4.2.1';
const OID_SHA256_WITH_RSA = '1.2.840.113549.1.1.11';
const OID_CONTENT_TYPE    = '1.2.840.113549.1.9.3';
const OID_SIGNING_TIME    = '1.2.840.113549.1.9.5';
const OID_MESSAGE_DIGEST  = '1.2.840.113549.1.9.4';

const { asn1 } = forge;
const U   = asn1.Class.UNIVERSAL;
const CTX = asn1.Class.CONTEXT_SPECIFIC;
const SEQ  = (ch) => asn1.create(U, asn1.Type.SEQUENCE,   true,  ch);
const SET_ = (ch) => asn1.create(U, asn1.Type.SET,        true,  ch);
const OID_ = (o)  => asn1.create(U, asn1.Type.OID,        false, asn1.oidToDer(o).getBytes());
const INT_ = (n)  => asn1.create(U, asn1.Type.INTEGER,     false, asn1.integerToDer(n).getBytes());
const OCT_ = (b)  => asn1.create(U, asn1.Type.OCTETSTRING, false, b);
const NULL_= ()   => asn1.create(U, asn1.Type.NULL,        false, '');
const CTX_ = (n, ch) => asn1.create(CTX, n, true, ch);
const algId = (o) => SEQ([OID_(o), NULL_()]);

const bytesToHex = (bytes) => Array.from(bytes)
  .map(b => b.toString(16).padStart(2, '0'))
  .join('');

const hexToBytes = (hex) => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
};

const uint8ToBinaryString = (bytes) => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return binary;
};

function verifyRsaSha256Signature(certDerHex, signedBytes, signatureHex) {
  try {
    const certDerBinary = forge.util.hexToBytes(certDerHex);
    const certAsn1 = asn1.fromDer(forge.util.createBuffer(certDerBinary));
    const cert = forge.pki.certificateFromAsn1(certAsn1);
    const md = forge.md.sha256.create();
    md.update(uint8ToBinaryString(signedBytes), 'raw');
    return cert.publicKey.verify(md.digest().bytes(), forge.util.hexToBytes(signatureHex));
  } catch (_error) {
    return false;
  }
}

// Build the RFC 5652 SignedAttributes SET and return its DER bytes as Uint8Array.
// The DER is encoded as a Universal SET (tag 0x31) — what gets hashed for signing.
// When embedded in the CMS SignerInfo it is re-tagged as [0] IMPLICIT (0xA0).
function buildSignedAttrsAsn1(contentHashHex, signingTime) {
  const pad = n => String(n).padStart(2, '0');
  const d = signingTime instanceof Date ? signingTime : new Date(signingTime);
  const utcVal = pad(d.getUTCFullYear() % 100) + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate()) +
    pad(d.getUTCHours()) + pad(d.getUTCMinutes()) + pad(d.getUTCSeconds()) + 'Z';
  return SET_([
    SEQ([OID_(OID_CONTENT_TYPE),   SET_([OID_(OID_DATA)])]),
    SEQ([OID_(OID_SIGNING_TIME),   SET_([asn1.create(U, asn1.Type.UTCTIME, false, utcVal)])]),
    SEQ([OID_(OID_MESSAGE_DIGEST), SET_([OCT_(forge.util.hexToBytes(contentHashHex))])]),
  ]);
}

function signedAttrsDerBytes(contentHashHex, signingTime) {
  const binStr = asn1.toDer(buildSignedAttrsAsn1(contentHashHex, signingTime)).getBytes();
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
  return bytes;
}

// attrsDerBytes = Uint8Array of DER-encoded signedAttrs SET (tag 0x31) produced by
// signedAttrsDerBytes(). Passing the raw bytes (rather than re-building) guarantees
// that what is embedded in the CMS is byte-for-byte identical to what was hashed
// for signing — only the outer tag is changed from 0x31 (SET) to 0xA0 ([0] IMPLICIT).
export function buildCmsDetached(certDerHex, signatureHex, attrsDerBytes = null) {
  const certDerBinary = forge.util.hexToBytes(certDerHex);
  const certAsn1 = asn1.fromDer(forge.util.createBuffer(certDerBinary));

  // Extract issuer Name and serialNumber INTEGER directly from the parsed ASN.1.
  // node-forge's DistinguishedName has no toAsn1() method; extracting from
  // certAsn1 also guarantees bit-for-bit DER accuracy for the serial number.
  // TBSCertificate is certAsn1.value[0]. When a version [0] EXPLICIT field is
  // present (all v3 certs), indices are: serial=1, signature=2, issuer=3.
  const tbs = certAsn1.value[0];
  const hasVersion = tbs.value[0].tagClass === asn1.Class.CONTEXT_SPECIFIC;
  const serialAsn1 = tbs.value[hasVersion ? 1 : 0];
  const issuerAsn1  = tbs.value[hasVersion ? 3 : 2];

  const issuerAndSerial = SEQ([issuerAsn1, serialAsn1]);

  // Re-tag the signedAttrs DER: 0x31 (Universal SET) → 0xA0 ([0] IMPLICIT CONSTRUCTED).
  // We use the exact bytes that were hashed to ensure what Adobe verifies matches what
  // the USB token signed — no second encoding pass that could diverge.
  let signedAttrsNode = null;
  if (attrsDerBytes) {
    const implicitDer = attrsDerBytes.slice();
    implicitDer[0] = 0xA0;
    const binStr = Array.from(implicitDer).map(b => String.fromCharCode(b)).join('');
    signedAttrsNode = asn1.fromDer(forge.util.createBuffer(binStr));
  }

  const signerInfoChildren = [
    INT_(1), issuerAndSerial,
    algId(OID_SHA256),
    ...(signedAttrsNode ? [signedAttrsNode] : []),
    algId(OID_SHA256_WITH_RSA),
    OCT_(forge.util.hexToBytes(signatureHex)),
  ];

  const signerInfo = SEQ(signerInfoChildren);

  const signedData = SEQ([
    INT_(1),
    SET_([algId(OID_SHA256)]),
    SEQ([OID_(OID_DATA)]),
    CTX_(0, [certAsn1]),
    SET_([signerInfo]),
  ]);

  return Buffer.from(
    asn1.toDer(SEQ([OID_(OID_SIGNED_DATA), CTX_(0, [signedData])])).getBytes(),
    'binary'
  );
}

function splitLongWord(word, font, size, maxWidth) {
  const parts = [];
  let current = '';

  for (const char of String(word || '')) {
    const candidate = `${current}${char}`;
    if (!current || font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      current = candidate;
    } else {
      parts.push(current);
      current = char;
    }
  }

  if (current) parts.push(current);
  return parts;
}

function wrapTextToWidth(text, font, size, maxWidth) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (!line || font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      line = candidate;
      return;
    }

    lines.push(line);

    if (font.widthOfTextAtSize(word, size) <= maxWidth) {
      line = word;
      return;
    }

    const parts = splitLongWord(word, font, size, maxWidth);
    lines.push(...parts.slice(0, -1));
    line = parts[parts.length - 1] || '';
  });

  if (line) lines.push(line);
  return lines;
}

function fitWrappedText({ text, font, maxWidth, maxLines, initialSize, minSize }) {
  let size = initialSize;
  let lines = [];

  while (size >= minSize) {
    lines = wrapTextToWidth(text, font, size, maxWidth);
    if (lines.length <= maxLines) {
      return { size, lines };
    }
    size -= 0.5;
  }

  return {
    size: minSize,
    lines: wrapTextToWidth(text, font, minSize, maxWidth).slice(0, maxLines),
  };
}

async function drawAppearanceCard({ pdfDoc, page, widgetRect, signerName, signerTitle, signatureReason, signatureLocation, signingTime, signatureAppearance }) {
  const [x1, y1, x2, y2] = widgetRect;
  const width = x2 - x1;
  const height = y2 - y1;
  if (width < 40 || height < 24) return;

  const { includeName = true, includeDate = true, includeLocation = true, includeReason = false, includeLabels = true, language = 'en' } = signatureAppearance || {};
  const isFr = String(language).toLowerCase().startsWith('fr');
  const L = isFr
    ? { by: 'Signe numeriquement par', title: 'Titre', date: 'Date', location: 'Emplacement', reason: 'Motif' }
    : { by: 'Digitally signed by', title: 'Title', date: 'Date', location: 'Location', reason: 'Reason' };
  const lbl = (key, val) => includeLabels ? `${L[key]}: ${val}` : val;
  const signedAt = signingTime.toISOString().replace('T', ' ').replace('Z', '');

  const lines = [];
  if (includeName) lines.push(lbl('by', signerName));
  if (signerTitle) lines.push(lbl('title', signerTitle));
  if (includeLocation && signatureLocation) lines.push(lbl('location', signatureLocation));
  if (includeReason && signatureReason) lines.push(lbl('reason', signatureReason));
  if (includeDate) lines.push(lbl('date', signedAt));

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const leftColWidth = Math.max(70, width * 0.38);
  const padding = 6;
  const lineHeight = Math.max(10, Math.min(14, height / 7));
  const contentTop = y2 - padding - 2;

  page.drawRectangle({
    x: x1,
    y: y1,
    width,
    height,
    borderColor: rgb(0.74, 0.74, 0.74),
    borderWidth: 0.8,
  });
  page.drawLine({
    start: { x: x1 + leftColWidth, y: y1 + padding },
    end: { x: x1 + leftColWidth, y: y2 - padding },
    thickness: 0.5,
    color: rgb(0.74, 0.74, 0.74),
  });

  const watermarkText = 'CERTEUROPE';
  let watermarkSize = Math.min(22, height * 0.24);
  while (watermarkSize > 10 && bold.widthOfTextAtSize(watermarkText, watermarkSize) > width * 0.8) {
    watermarkSize -= 1;
  }
  page.drawText(watermarkText, {
    x: x1 + width * 0.18,
    y: y1 + height * 0.38,
    size: watermarkSize,
    font: bold,
    color: rgb(0.78, 0.88, 0.96),
    opacity: 0.42,
  });

  const nameColWidth = Math.max(24, leftColWidth - 2 * padding);
  const nameFit = fitWrappedText({
    text: signerName || 'Signer',
    font: bold,
    maxWidth: nameColWidth,
    maxLines: Math.max(2, Math.floor((height - padding * 2) / 12)),
    initialSize: Math.min(14, Math.max(8, height / 8)),
    minSize: 7,
  });
  const nameLineHeight = nameFit.size * 1.25;

  let nameY = contentTop;
  nameFit.lines.forEach(line => {
    page.drawText(line, { x: x1 + padding, y: Math.max(y1 + padding, nameY), size: nameFit.size, font: bold, color: rgb(0.12, 0.12, 0.12) });
    nameY -= nameLineHeight;
  });

  const rightColWidth = width - leftColWidth - 3 * padding;
  let textY = contentTop;
  const availableTextLines = Math.max(1, Math.floor((height - padding * 2) / lineHeight));
  const wrappedLines = [];
  lines.forEach(line => {
    wrappedLines.push(...wrapTextToWidth(line, font, 9, rightColWidth));
  });

  wrappedLines.slice(0, availableTextLines).forEach(display => {
    page.drawText(display, { x: x1 + leftColWidth + padding, y: Math.max(y1 + padding, textY), size: 9, font, color: rgb(0.23, 0.23, 0.23) });
    textY -= lineHeight;
  });
}

// BridgeSigner receives the PDF buffer AFTER SignPdf has filled in the real ByteRange.
class BridgeSigner extends Signer {
  constructor({ certId, slot, pin, certDerHex, signingTime }) {
    super();
    this._certId = certId;
    this._slot = slot;
    this._pin = pin;
    this._certDerHex = certDerHex;
    this._signingTime = signingTime instanceof Date ? signingTime : new Date();
  }

  async sign(pdfBuffer) {
    // @signpdf v3 strips the signature placeholder before calling signer.sign(), giving
    // us original[0:L1] + original[S2:S2+L2] — exactly the bytes Adobe will hash.
    // Hashing pdfBuffer directly avoids the mistake of re-slicing using original offsets
    // (which would read from wrong positions in the condensed buffer).
    const pdfBytes = pdfBuffer instanceof Uint8Array ? pdfBuffer : new Uint8Array(pdfBuffer);
    const contentHashBuf = await window.crypto.subtle.digest('SHA-256', pdfBytes);
    const contentHashHex = bytesToHex(new Uint8Array(contentHashBuf));

    // Per RFC 5652, what the token signs is SHA-256(DER(signedAttrs)), not the raw content hash.
    // signedAttrs includes contentType, signingTime, and messageDigest=contentHash.
    const attrsDer = signedAttrsDerBytes(contentHashHex, this._signingTime);
    const attrsHashBuf = await window.crypto.subtle.digest('SHA-256', attrsDer);
    const attrsHashHex = bytesToHex(new Uint8Array(attrsHashBuf));

    let signatureHex = '';
    const attrsRes = await fetch(`${BRIDGE_URL}/sign-attrs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        certId: this._certId,
        slot: this._slot,
        pin: this._pin,
        dataHex: bytesToHex(attrsDer),
      }),
    }).catch(() => null);

    if (attrsRes?.ok) {
      ({ signatureHex } = await attrsRes.json());
    } else {
      const bridgeRes = await fetch(`${BRIDGE_URL}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ certId: this._certId, slot: this._slot, pin: this._pin, hashHex: attrsHashHex }),
      });
      if (!bridgeRes.ok) {
        const err = await bridgeRes.json().catch(() => ({}));
        throw new Error(`USB token signing failed: ${err.error || bridgeRes.statusText}`);
      }
      ({ signatureHex } = await bridgeRes.json());
    }

    const signatureBytes = hexToBytes(signatureHex);
    if (!signatureBytes.length || !verifyRsaSha256Signature(this._certDerHex, attrsDer, signatureHex)) {
      throw new Error('USB token returned a signature that does not verify against the selected certificate.');
    }

    // Pass the exact attrsDer bytes used for hashing — buildCmsDetached re-tags them
    // 0x31→0xA0 in-place so what the verifier hashes is bit-for-bit identical.
    return buildCmsDetached(this._certDerHex, signatureHex, attrsDer);
  }
}

/**
 * Sign a PDF client-side using the USB token via the local bridge.
 * Draws the appearance card, adds the placeholder, then delegates
 * to BridgeSigner via SignPdf for correct ByteRange handling.
 * Returns the final signed PDF as a Uint8Array.
 */
export async function signPdfWithBridge(pdfBytes, {
  signerName = 'Signer', signerTitle = '', signerContactInfo = '',
  signatureReason = 'Certified translation approval',
  signatureLocation = 'A&H Solutions',
  signaturePageIndex = 0, signatureWidgetRect = null,
  signatureAppearance = null, signatureAppearanceImage = null,
  signingTime = new Date(),
  signatureLength = DEFAULT_SIGNATURE_LENGTH,
  certId, slot, pin, certDerHex,
} = {}) {
  const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  const pages = pdfDoc.getPages();
  const page = pages[signaturePageIndex] ?? pages[0];
  const widgetRect = signatureWidgetRect ?? [36, 36, 280, 156];

  if (signatureAppearanceImage) {
    const base64 = signatureAppearanceImage.split(',')[1];
    const pngBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const pngImage = await pdfDoc.embedPng(pngBytes);
    const [rx1, ry1, rx2, ry2] = widgetRect;
    page.drawImage(pngImage, { x: rx1, y: ry1, width: rx2 - rx1, height: ry2 - ry1 });
  } else if (signatureAppearance) {
    await drawAppearanceCard({ pdfDoc, page, widgetRect, signerName, signerTitle, signatureReason, signatureLocation, signingTime, signatureAppearance });
  }

  pdflibAddPlaceholder({ pdfPage: page, reason: signatureReason, contactInfo: signerContactInfo || signerName, name: signerName, location: signatureLocation, signingTime, signatureLength, widgetRect, appName: 'Juree Certificate Signing' });

  const preparedBytes = Buffer.from(await pdfDoc.save({ useObjectStreams: false, updateMetadata: false }));
  const signer = new BridgeSigner({ certId, slot, pin, certDerHex, signingTime });
  const signedBuffer = await new SignPdf().sign(preparedBytes, signer);
  return new Uint8Array(signedBuffer);
}
